import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Check, ArrowRight, ArrowLeft, Sparkles, Loader2, CalendarCheck } from "lucide-react";
import { toast } from "sonner";

const AUDIENCES = ["investor", "lender", "tenant", "buyer", "hotel guest", "resident", "municipality/community"];
const GOALS = ["capital raise", "leasing", "sales", "bookings", "investor presentation", "entitlement/community", "launch", "announcement"];
const ASSETS = ["exterior renderings", "interior renderings", "floorplans", "elevations", "site plan", "schematics", "photos", "aerials", "diagrams", "brand files", "project deck", "logo"];
const RATIOS = ["16:9", "9:16", "4:5", "1:1"];
const DERIVS = ["60s cut", "30s cut", "15s teaser"];
const BUDGETS = ["$2,500–$5,000", "$5,000–$10,000", "$10,000–$20,000", "$20,000+"];

const Chip = ({ active, onClick, children, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`px-4 py-2.5 rounded-full text-sm border transition-all
      ${active ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "bg-[#12141D] text-slate-300 border-slate-700 hover:border-[#D4AF37]/50"}`}>
    {children}
  </button>
);

export default function CustomVision() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [booking, setBooking] = useState({ preferred_date: "", preferred_time: "", notes: "" });
  const [booked, setBooked] = useState(false);
  const [f, setF] = useState({
    contact_name: "", contact_email: "", contact_phone: "",
    project_name: "", location: "", property_type: "", current_stage: "", website: "", target_date: "",
    audiences: [], primary_goal: "", available_assets: [],
    desired_length: "", narration: false, music: true, cta: "", deadline: "", aspect_ratios: [], derivatives: [],
    budget_tier: "", extra_notes: "",
  });

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const toggle = (k, v) => setF((s) => ({ ...s, [k]: s[k].includes(v) ? s[k].filter((x) => x !== v) : [...s[k], v] }));

  const steps = ["Project", "Audience & Goal", "Assets", "Creative", "Budget & Contact"];
  const progress = ((step + 1) / steps.length) * 100;

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/custom/intake", f);
      setResult(data);
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const submitBooking = async () => {
    try {
      await api.post(`/custom/${result.id}/book`, booking);
      setBooked(true);
      toast.success("Call scheduled — our team has your full brief");
    } catch (err) { toast.error("Failed to book"); }
  };

  if (result) {
    const a = result.analysis;
    return (
      <div className="bg-[#090A0F] min-h-screen"><Header />
        <div className="max-w-3xl mx-auto px-6 pt-32 pb-16 fade-up" data-testid="custom-result">
          <div className="text-center mb-10">
            <Sparkles className="w-10 h-10 text-[#D4AF37] mx-auto mb-4" />
            <h1 className="font-display text-4xl text-white mb-2">Your project has been scoped</h1>
            <p className="text-slate-400">Our AI Scope Analyst reviewed your brief. A producer will confirm details on a call.</p>
          </div>

          <div className="glass rounded-2xl p-8 mb-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display text-2xl text-white">Recommended investment</h3>
              <span className="font-mono text-3xl text-[#D4AF37]">${a.recommended_price_target.toLocaleString()}</span>
            </div>
            <p className="text-sm text-slate-400 mb-1">Estimated range</p>
            <p className="font-mono text-white mb-5">${a.recommended_price_low.toLocaleString()} – ${a.recommended_price_high.toLocaleString()}</p>
            <p className="text-sm text-slate-300 mb-4">{a.summary}</p>
            <p className="text-xs text-slate-500 mb-2">Recommended deliverables</p>
            <ul className="space-y-1.5 mb-4">
              {a.recommended_deliverables.map((d, i) => (
                <li key={i} className="text-sm text-slate-300 flex items-center gap-2"><Check className="w-4 h-4 text-[#D4AF37]" /> {d}</li>
              ))}
            </ul>
            <p className="text-xs text-slate-500">Timeline: <span className="text-slate-300">{a.recommended_timeline}</span></p>
          </div>

          {!booked ? (
            <div className="glass rounded-2xl p-8" data-testid="booking-panel">
              <h3 className="font-display text-2xl text-white mb-4">Schedule your project call</h3>
              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <Label className="text-slate-300">Preferred date</Label>
                  <Input type="date" value={booking.preferred_date} onChange={(e) => setBooking({ ...booking, preferred_date: e.target.value })}
                    className="mt-1.5 bg-[#12141D] border-slate-700 text-white" data-testid="booking-date" />
                </div>
                <div>
                  <Label className="text-slate-300">Preferred time</Label>
                  <Input type="time" value={booking.preferred_time} onChange={(e) => setBooking({ ...booking, preferred_time: e.target.value })}
                    className="mt-1.5 bg-[#12141D] border-slate-700 text-white" data-testid="booking-time" />
                </div>
              </div>
              <Button onClick={submitBooking} disabled={!booking.preferred_date || !booking.preferred_time}
                className="w-full bg-[#D4AF37] text-black hover:bg-[#E2C044] rounded-full h-12" data-testid="submit-booking">
                <CalendarCheck className="w-4 h-4 mr-2" /> Schedule Call
              </Button>
            </div>
          ) : (
            <div className="glass rounded-2xl p-8 text-center" data-testid="booking-confirmed">
              <CalendarCheck className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
              <h3 className="font-display text-2xl text-white mb-1">You're booked</h3>
              <p className="text-slate-400">A producer will reach out at {booking.preferred_time} on {booking.preferred_date}.</p>
              <Button onClick={() => navigate("/")} variant="outline" className="mt-6 border-[#D4AF37]/40 text-white rounded-full">Back home</Button>
            </div>
          )}
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-[#090A0F] min-h-screen"><Header />
      <div className="max-w-3xl mx-auto px-6 pt-32 pb-16 fade-up">
        <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#D4AF37] mb-2">Custom Vision · Build a Custom Film</p>
        <h1 className="font-display text-4xl text-white mb-6">Let's scope your project</h1>
        <div className="flex items-center justify-between mb-2 text-xs text-slate-400">
          <span>{steps[step]}</span><span>Step {step + 1} of {steps.length}</span>
        </div>
        <Progress value={progress} className="h-1.5 bg-slate-800 mb-10 [&>div]:bg-[#D4AF37]" />

        <div className="glass rounded-2xl p-8 min-h-[360px]" data-testid={`wizard-step-${step}`}>
          {step === 0 && (
            <div className="space-y-4">
              <div><Label className="text-slate-300">Project / property name</Label>
                <Input value={f.project_name} onChange={(e) => set("project_name", e.target.value)} className="mt-1.5 bg-[#12141D] border-slate-700 text-white" data-testid="cv-project-name" /></div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label className="text-slate-300">Location</Label>
                  <Input value={f.location} onChange={(e) => set("location", e.target.value)} className="mt-1.5 bg-[#12141D] border-slate-700 text-white" data-testid="cv-location" /></div>
                <div><Label className="text-slate-300">Property type</Label>
                  <Input value={f.property_type} onChange={(e) => set("property_type", e.target.value)} placeholder="Hotel, tower, estate…" className="mt-1.5 bg-[#12141D] border-slate-700 text-white" data-testid="cv-type" /></div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label className="text-slate-300">Current stage</Label>
                  <Input value={f.current_stage} onChange={(e) => set("current_stage", e.target.value)} placeholder="Pre-construction, complete…" className="mt-1.5 bg-[#12141D] border-slate-700 text-white" data-testid="cv-stage" /></div>
                <div><Label className="text-slate-300">Opening / completion target</Label>
                  <Input value={f.target_date} onChange={(e) => set("target_date", e.target.value)} className="mt-1.5 bg-[#12141D] border-slate-700 text-white" data-testid="cv-target" /></div>
              </div>
              <div><Label className="text-slate-300">Website</Label>
                <Input value={f.website} onChange={(e) => set("website", e.target.value)} className="mt-1.5 bg-[#12141D] border-slate-700 text-white" data-testid="cv-website" /></div>
            </div>
          )}
          {step === 1 && (
            <div className="space-y-6">
              <div><Label className="text-slate-300 mb-3 block">Who is the audience? (select all)</Label>
                <div className="flex flex-wrap gap-2">{AUDIENCES.map((x) => <Chip key={x} active={f.audiences.includes(x)} onClick={() => toggle("audiences", x)} testid={`cv-aud-${x}`}>{x}</Chip>)}</div></div>
              <div><Label className="text-slate-300 mb-3 block">Primary goal</Label>
                <div className="flex flex-wrap gap-2">{GOALS.map((x) => <Chip key={x} active={f.primary_goal === x} onClick={() => set("primary_goal", x)} testid={`cv-goal-${x}`}>{x}</Chip>)}</div></div>
            </div>
          )}
          {step === 2 && (
            <div><Label className="text-slate-300 mb-3 block">What assets do you have? (select all)</Label>
              <div className="flex flex-wrap gap-2">{ASSETS.map((x) => <Chip key={x} active={f.available_assets.includes(x)} onClick={() => toggle("available_assets", x)} testid={`cv-asset-${x.replace(/\s/g,'-')}`}>{x}</Chip>)}</div></div>
          )}
          {step === 3 && (
            <div className="space-y-6">
              <div><Label className="text-slate-300">Desired video length</Label>
                <Input value={f.desired_length} onChange={(e) => set("desired_length", e.target.value)} placeholder="e.g. 90 seconds" className="mt-1.5 bg-[#12141D] border-slate-700 text-white" data-testid="cv-length" /></div>
              <div><Label className="text-slate-300 mb-3 block">Aspect ratios</Label>
                <div className="flex flex-wrap gap-2">{RATIOS.map((x) => <Chip key={x} active={f.aspect_ratios.includes(x)} onClick={() => toggle("aspect_ratios", x)} testid={`cv-ratio-${x}`}>{x}</Chip>)}</div></div>
              <div><Label className="text-slate-300 mb-3 block">Derivative videos</Label>
                <div className="flex flex-wrap gap-2">{DERIVS.map((x) => <Chip key={x} active={f.derivatives.includes(x)} onClick={() => toggle("derivatives", x)} testid={`cv-deriv-${x.replace(/\s/g,'-')}`}>{x}</Chip>)}</div></div>
              <div className="flex gap-3">
                <Chip active={f.narration} onClick={() => set("narration", !f.narration)} testid="cv-narration">Custom narration</Chip>
                <Chip active={f.music} onClick={() => set("music", !f.music)} testid="cv-music">Music</Chip>
              </div>
            </div>
          )}
          {step === 4 && (
            <div className="space-y-5">
              <div><Label className="text-slate-300 mb-3 block">Budget</Label>
                <div className="flex flex-wrap gap-2">{BUDGETS.map((x) => <Chip key={x} active={f.budget_tier === x} onClick={() => set("budget_tier", x)} testid={`cv-budget-${x.replace(/[^0-9+]/g,'')}`}>{x}</Chip>)}</div></div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label className="text-slate-300">Your name</Label>
                  <Input value={f.contact_name} onChange={(e) => set("contact_name", e.target.value)} className="mt-1.5 bg-[#12141D] border-slate-700 text-white" data-testid="cv-contact-name" /></div>
                <div><Label className="text-slate-300">Email</Label>
                  <Input type="email" value={f.contact_email} onChange={(e) => set("contact_email", e.target.value)} className="mt-1.5 bg-[#12141D] border-slate-700 text-white" data-testid="cv-contact-email" /></div>
              </div>
              <div><Label className="text-slate-300">Anything else?</Label>
                <Textarea value={f.extra_notes} onChange={(e) => set("extra_notes", e.target.value)} className="mt-1.5 bg-[#12141D] border-slate-700 text-white" data-testid="cv-notes" /></div>
            </div>
          )}
        </div>

        <div className="flex justify-between mt-6">
          <Button variant="outline" disabled={step === 0} onClick={() => setStep(step - 1)}
            className="border-slate-700 text-slate-300 rounded-full disabled:opacity-30" data-testid="wizard-back">
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
          </Button>
          {step < steps.length - 1 ? (
            <Button onClick={() => setStep(step + 1)} className="bg-[#D4AF37] text-black hover:bg-[#E2C044] rounded-full" data-testid="wizard-next">
              Next <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          ) : (
            <Button onClick={submit} disabled={busy || !f.contact_email || !f.project_name}
              className="bg-[#D4AF37] text-black hover:bg-[#E2C044] rounded-full" data-testid="wizard-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Scope My Project <Sparkles className="w-4 h-4 ml-1.5" /></>}
            </Button>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
